import { useSnackbar, type CivilDate } from '@hue/design-system';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { getApiError, schedulingApi } from '../../lib/api.js';
import { AppointmentDialog } from './appointment-dialog.js';
import { useResourceOptions, useServiceOptions } from './scheduling-options.js';

/// O Novo agendamento de um serviço já vendido, aberto pela venda (ADR 0046, decisão 4). Cliente e
/// serviço vêm da venda; o pedido não pede sinal e o pagamento continua nela.
export function SoldServiceBooking({
  saleItemUnitId,
  today,
  timeZone,
  onClose,
}: {
  saleItemUnitId: string;
  today: CivilDate;
  timeZone: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const sold = useQuery({
    queryKey: ['company', 'scheduling', 'sold-service', saleItemUnitId],
    queryFn: () => schedulingApi.soldService(saleItemUnitId),
    enabled: Boolean(saleItemUnitId),
    retry: false,
  });
  const resources = useResourceOptions();
  const services = useServiceOptions();
  // O diálogo recomeça o formulário quando a semente muda: ela precisa ser a mesma entre renders.
  const seed = useMemo(() => (sold.data ? { sold: sold.data } : undefined), [sold.data]);
  const failure = sold.error ? getApiError(sold.error) : null;
  useEffect(() => {
    if (!failure) return;
    snackbar.failure(failure);
    onClose();
  }, [failure, onClose, snackbar]);
  if (!seed) return null;
  return (
    <AppointmentDialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      appointment={null}
      seed={seed}
      today={today}
      timeZone={timeZone}
      services={services.data?.services ?? []}
      professionals={(resources.data ?? []).filter(
        (item) => item.kind === 'PROFESSIONAL' && item.attendsSchedule !== false,
      )}
      rooms={(resources.data ?? []).filter((item) => item.kind === 'ROOM')}
      onSaved={async () => {
        await queryClient.invalidateQueries({ queryKey: ['company'] });
        onClose();
        snackbar.success('Agendamento criado com o serviço já pago.');
      }}
    />
  );
}
